<script setup lang="ts">
import type { AvatarProps, Profile } from '~/types/profile'
import { ResourceType } from '~/types/common'
import { useRouter } from '#imports'
import { location, joined_at, active_at } from '~/helpers/profiles'
import SkeletonLoader from '~/components/SkeletonLoader.vue'
import type { ToastNotification } from './Toast.vue'
import { useProfileSelfStore } from '~/stores/user/Self'

const router = useRouter()
const { $navigate } = useNuxtApp()

const isAuthed = inject('isAuthed') as Ref<boolean>
const self = inject('profile') as Ref<Profile>
const toast = inject('toast') as Ref<ToastNotification>
const profileSelfStore = useProfileSelfStore()
const props = defineProps<AvatarProps>()

const initials = computed(() => {
	if (props.profile.username) {
		return props.profile.username[0] + props.profile.username[1]
	}

	if (props.profile.first_name && props.profile.last_name) {
		return props.profile.first_name[0] + props.profile.last_name[0]
	}

	if (props.profile.first_name) {
		return props.profile.first_name[0] + props.profile.first_name[1]
	}

	if (props.profile.last_name) {
		return props.profile.last_name[0] + props.profile.last_name[1]
	}

	return ''
})

const textSize = computed(() => {
	switch (props.size) {
		case 6:
			return 'text-sm'
		case 8:
			return 'text-base'
		case 10:
			return 'text-lg'
		case 12:
			return 'text-xl'
		case 14:
			return 'text-1xl'
		case 16:
			return 'text-2xl'
		case 18:
			return 'text-3xl'
		case 20:
			return 'text-4xl'
		default:
			return 'text-sm'
	}
})

const starred = ref<boolean>(false)

if (isAuthed.value && self.value?.users_subscriptions) {
	starred.value = self.value.users_subscriptions.some(
		sub => sub.resource_type === ResourceType.USER && sub.resource_id === props.profile.id,
	)
}

async function star() {
	if (!isAuthed.value) {
		const title = 'Login To Follow'
		const message = `Please login (or register for free) to follow this user. Once completed, you will be redirected back to this page.`
		$navigate(`/login?redirect=${router.currentRoute.value.fullPath}&toast_title=${encodeURI(title)}&toast_message=${encodeURI(message)}`)
	} else {
		try {
			if (starred.value) {
				const id = self.value.users_subscriptions.find(
					sub => sub.resource_type === ResourceType.USER && sub.resource_id === props.profile.id,
				)?.id
				if (!id) throw new Error('Subscription not found')

				await profileSelfStore().unsubscribe(id)
				toast.value = {
					show: true,
					timeout: 10,
					title: `You've unfollowed this user`,
					message: `You will no longer receive notifications about this user.`,
					type: 'success',
				}
				starred.value = false
			} else {
				await profileSelfStore().subscribe({
					user_id: self.value.id,
					resource_type: ResourceType.USER,
					resource_id: props.profile.id,
				})

				toast.value = {
					show: true,
					timeout: 10,
					title: `You're following this user`,
					message: `You will now receive notifications about this user, you can manage notification settings in your profile.`,
					type: 'success',
				}
				starred.value = true
			}
		} catch (e) {
			console.error(e)
			toast.value = {
				show: true,
				timeout: 10,
				title: `Error updating follower`,
				message: `There was an error updating your follower for this user, please try again later.`,
				type: 'error',
			}
		}
	}
}

</script>

<template>
	<div class="group/avatar z-2 relative">
		<template v-if="isLoading">
			<div data-test="avatar-skeleton">
				<SkeletonLoader
					:style="`height: ${(size ?? 10) / 4}rem; min-width: ${(size ?? 10) / 4}rem;`"
					:type="type === 'rounded' ? 'rectangle' : 'circle'"
					bg-class="bg-gray-300"
					:css-class="type === 'rounded' ? 'rounded-md' : 'rounded-full'"
					data-test="avatar-image-skeleton" />
			</div>
		</template>
		<template v-else>
			<NuxtLink v-if="!hideLink" :to="to ?? '#'">
				<span v-if="profile.avatar_url" class="inline-block">
					<img
						:style="`height: ${(size ?? 10) / 4}rem; min-width: ${(size ?? 10) / 4}rem;`"
						:class="`inline-block ${type === 'rounded' ? 'rounded-md' : 'rounded-full'}`"
						:src="profile.avatar_url"
						:alt="profile.username ?? initials" />
				</span>
				<span
					v-else
					:style="`height: ${(size ?? 10) / 4}rem; min-width: ${(size ?? 10) / 4}rem;`"
					:class="`inline-flex ${
						type === 'rounded' ? 'rounded-md' : 'rounded-full'
					} items-center justify-center bg-green-500`">
					<span :class="`${textSize} font-medium text-white`">{{ initials.toUpperCase() }}</span>
				</span>
			</NuxtLink>
			<div v-else>
				<span v-if="profile.avatar_url" class="inline-block">
					<img
						:style="`height: ${(size ?? 10) / 4}rem; min-width: ${(size ?? 10) / 4}rem;`"
						:class="`inline-block ${type === 'rounded' ? 'rounded-md' : 'rounded-full'}`"
						:src="profile.avatar_url"
						:alt="profile.username ?? initials" />
				</span>
				<span
					v-else
					:style="`height: ${(size ?? 10) / 4}rem; min-width: ${(size ?? 10) / 4}rem;`"
					:class="`inline-flex ${
						type === 'rounded' ? 'rounded-md' : 'rounded-full'
					} items-center justify-center bg-green-500`">
					<span :class="`${textSize} font-medium text-white`">{{ initials.toUpperCase() }}</span>
				</span>
			</div>
		</template>

		<div v-if="!hidePopover" id="avatarCallout" class="hidden group-hover/avatar:block absolute z-50">
			<div
				class="max-w-full mb-5 sm:mb-0 mr-5 sm:mr-0 flex-auto overflow-hidden rounded bg-white text-sm/6 shadow-lg ring-1 ring-gray-100 flex flex-col">
				<template v-if="isLoading">
					<div class="flex flex-row gap-6 p-5">
						<SkeletonLoader
							:style="`height: 7rem; width: 7rem;`"
							:type="type === 'rounded' ? 'rectangle' : 'circle'"
							bg-class="bg-gray-300"
							:css-class="type === 'rounded' ? 'rounded-md' : 'rounded-full'"
							data-test="hover-avatar-skeleton" />
						<div class="flex flex-col gap-2">
							<SkeletonLoader
								data-test="hover-username-skeleton"
								class="h-7 w-48"
								bg-class="bg-gray-300"
								css-class="rounded-md" />
							<div class="flex flex-row gap-2">
								<SkeletonLoader
									data-test="hover-location-icon-skeleton"
									class="h-5 w-5"
									bg-class="bg-gray-300"
									css-class="rounded-md" />
								<SkeletonLoader class="h-5 w-32" bg-class="bg-gray-300" css-class="rounded-md" />
							</div>
							<div class="flex flex-row gap-2">
								<SkeletonLoader
									data-test="hover-joined-icon-skeleton"
									class="h-5 w-5"
									bg-class="bg-gray-300"
									css-class="rounded-md" />
								<SkeletonLoader class="h-5 w-32" bg-class="bg-gray-300" css-class="rounded-md" />
							</div>
							<div class="flex flex-row gap-2">
								<SkeletonLoader
									data-test="hover-active-icon-skeleton"
									class="h-5 w-5"
									bg-class="bg-gray-300"
									css-class="rounded-md" />
								<SkeletonLoader class="h-5 w-32" bg-class="bg-gray-300" css-class="rounded-md" />
							</div>
						</div>
					</div>

					<div class="pb-5 px-5">
						<SkeletonLoader
							data-test="hover-bio-skeleton"
							class="h-16 w-full"
							bg-class="bg-gray-300"
							css-class="rounded-md" />
					</div>
				</template>
				<template v-else>
					<div class="flex flex-row gap-6 p-5">
						<span v-if="profile.avatar_url" class="inline-block">
							<img
								:style="`height: 7rem; width: 7rem;`"
								:class="`inline-block ${type === 'rounded' ? 'rounded-md' : 'rounded-full'}`"
								:src="profile.avatar_url"
								:alt="profile.username ?? initials" />
						</span>
						<span
							v-else
							:style="`height: ${(size ?? 10) / 2}rem; width: ${(size ?? 10) / 2}rem;`"
							:class="`inline-flex ${
								type === 'rounded' ? 'rounded-md' : 'rounded-full'
							} items-center justify-center bg-green-500`">
							<span :class="`${textSize} font-medium text-white`">{{ initials.toUpperCase() }}</span>
						</span>

						<div class="flex flex-col gap-2">
							<h3 class="text-lg font-semibold text-gray-900 my-0">{{ profile.username }}</h3>
							<div class="flex flex-row gap-2">
								<Icon
									name="ph:map-pin-duotone"
									class="size-5 flex-none text-gray-400"
									aria-hidden="true" />
								<span class="text-gray-500">{{ location(profile) }}</span>
							</div>
							<div class="flex flex-row gap-2">
								<Icon
									name="ph:calendar-duotone"
									class="size-5 flex-none text-gray-400"
									aria-hidden="true" />
								<span class="text-gray-500">{{ joined_at(profile) }}</span>
							</div>
							<div class="flex flex-row gap-2">
								<Icon
									name="ph:clock-duotone"
									class="size-5 flex-none text-gray-400"
									aria-hidden="true" />
								<span class="text-gray-500">{{ active_at(profile) }}</span>
							</div>
						</div>
					</div>

					<div class="pb-5 px-5">
						<p class="text-sm text-gray-500 bio-description">{{ profile.bio }}</p>
					</div>
				</template>

				<div class="grid grid-cols-2 divide-x divide-gray-900/5 bg-gray-50">
					<NuxtLink
						v-if="self?.id !== profile.id"
						:to="`/profile/${props.profile.username}`"
						class="flex items-center justify-center gap-x-2.5 p-3 font-semibold text-gray-900 hover:bg-gray-100">
						<Icon name="ph:user-circle-duotone" class="size-5 flex-none text-gray-400" aria-hidden="true" />
						Visit Profile
					</NuxtLink>

					<NuxtLink
						v-if="self?.id !== profile.id && !starred"
						class="flex items-center justify-center gap-x-2.5 p-3 font-semibold text-gray-900 hover:bg-gray-100"
						@click="star">
						<Icon name="ph:star" class="size-5 flex-none text-gray-400" aria-hidden="true" />
						Follow
					</NuxtLink>

					<NuxtLink
						v-else-if="self?.id !== profile.id"
						class="flex items-center justify-center gap-x-2.5 p-3 font-semibold text-gray-900 hover:bg-gray-100"
						@click="star">
						<Icon name="ph:star-fill" class="size-5 flex-none text-gray-400" aria-hidden="true" />
						Unfollow
					</NuxtLink>

					<NuxtLink
						v-if="self?.id === profile.id"
						:to="`/profile/${self.username}`"
						class="flex items-center justify-center gap-x-2.5 p-3 font-semibold text-gray-900 hover:bg-gray-100">
						<Icon name="ph:user-circle-duotone" class="size-5 flex-none text-gray-400" aria-hidden="true" />
						Public Profile
					</NuxtLink>

					<NuxtLink
						v-if="self?.id === profile.id"
						to="/profile"
						class="flex items-center justify-center gap-x-2.5 p-3 font-semibold text-gray-900 hover:bg-gray-100">
						<Icon name="ph:pencil-duotone" class="size-5 flex-none text-gray-400" aria-hidden="true" />
						Edit Profile
					</NuxtLink>
				</div>
			</div>
		</div>
	</div>
</template>
<style>
#avatarCallout {
	width: 21rem;
}

.bio-description {
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 3; /* Number of lines displayed before it truncate */
}
</style>
